import {
  Button,
  ButtonColor,
  ButtonSize,
  Modal,
  ModalType,
  Table,
  TableRow,
  TextInput,
} from '@code/innovance-react-ui-kit';
import { yupResolver } from '@hookform/resolvers';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { CustomerTaxInfo, TaxInfoPageValidation } from 'src/front/api/apiInstance';
import { ReferenceDataType } from 'src/front/api/generated';
import usePRM from 'src/front/common/hooks/usePRM';
import {
  dictionaryLineFormat,
  formatNumeric,
  optionFormat,
} from 'src/front/common/utils/formUtils';
import { TaxNumberSchema } from 'src/front/common/utils/validationSchemas';

import {
  Container,
  Form,
  FormElement,
  FormRow,
  Separator,
  Subtitle,
  TableWrapper,
  Title,
  WarningText,
} from 'src/front/components/FormStyles';
import uid from 'uid';
import TaxModal, { TaxFormData } from './TaxModal';

const taxTableHeaders = [
  'cardEntry_taxInfo_taxResidenceCountry_label',
  'cardEntry_taxInfo_iinnProvided_label',
  'cardEntry_taxInfo_iinn_label',
];

interface FormData {
  taxNumber: string;
}

const defaultValues = {
  taxNumber: '',
};

interface Props {
  customerId?: string;
  goToNextStep: (customerId?: string) => void;
  disablePage?: boolean;
}

type Tax = TaxFormData;

const TaxInformation: FC<Props> = ({ customerId, goToNextStep, disablePage }) => {
  const { t } = useTranslation();

  const [showTaxModal, setShowTaxModal] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);

  const [warning, setWarning] = useState<string>();
  const [taxes, setTaxes] = useState<Tax[]>([]);
  const [taxFormValues, setTaxFormValues] = useState<Tax>();
  const [selectedTax, setSelectedTax] = useState<TableRow>();
  const [loadingTable, setLoadingTable] = useState<boolean>(true);

  const countries = usePRM(ReferenceDataType.COUNTRIES);
  const yesNoComboItems = usePRM(ReferenceDataType.ISPROVIDED);

  const { handleSubmit, errors, setValue, control, formState } = useForm<FormData>({
    defaultValues,
    resolver: yupResolver(TaxNumberSchema),
  });

  const loadTaxInformation = useCallback(async () => {
    const { data } = await TaxInfoPageValidation.getTaxInfoPageData();

    if (data.result === 'OK' && data.pageData) {
      const { taxResidencyList, taxNumber } = data.pageData;

      const taxList =
        taxResidencyList?.map((taxResidency) => ({
          taxResidenceCountry: optionFormat(taxResidency.taxResidenceCountry),
          hasTaxNumberProvided: optionFormat(taxResidency.hasTaxNumberProvided),
          foreignTaxNumberId: taxResidency.isNew ? uid() : taxResidency.foreignTaxNumberId,
          differentNumber: taxResidency.differentNumber || false,
          foreignTaxNumber: taxResidency.foreignTaxNumber || '',
          isNew: taxResidency.isNew,
        })) || [];
      setValue('taxNumber', taxNumber || '');
      setTaxes(taxList);
    } else {
      const { data: response } = await CustomerTaxInfo.getCustomerTaxInfo({ customerId });

      if (response.result === 'OK') {
        const [noOption, yesOption] = yesNoComboItems;

        const taxList =
          response.taxResidencyList?.map((tax) => ({
            taxResidenceCountry:
              countries.find((country) => country.value === tax.taxResidenceCountryCode) || null,
            hasTaxNumberProvided: tax.hasTaxNumberProvided ? yesOption : noOption,
            differentNumber: tax.differentNumber || false,
            foreignTaxNumber: tax.foreignTaxNumber || '',
            foreignTaxNumberId: tax.foreignTaxNumberId,
            isNew: tax.isNew,
          })) || [];

        setValue('taxNumber', response.taxNumber || '');
        setTaxes(taxList);
      }
    }
    setLoadingTable(false);
  }, [countries, customerId, setValue, yesNoComboItems]);

  useEffect(() => {
    if (countries.length && yesNoComboItems.length) {
      loadTaxInformation();
    }
  }, [customerId, countries.length, loadTaxInformation, yesNoComboItems.length]);

  const onSubmit = async (form: FormData): Promise<void> => {
    const hasTaxResidency = taxes.length > 0;
    if (hasTaxResidency) {
      warning && setWarning(undefined);

      const { data } = await TaxInfoPageValidation.validateTaxInfoPageData({
        taxNumber: form.taxNumber,
        taxResidencyList: taxes.map((tax) => ({
          taxResidenceCountry: dictionaryLineFormat(tax.taxResidenceCountry),
          hasTaxNumberProvided: dictionaryLineFormat(tax.hasTaxNumberProvided),
          foreignTaxNumber: tax.foreignTaxNumber,
          differentNumber: tax.differentNumber,
          isNew: tax.isNew || false,
          ...(tax.isNew ? {} : { foreignTaxNumberId: tax.foreignTaxNumberId }),
        })),
      });

      if (data.result === 'OK') {
        goToNextStep(customerId);
      }
    } else {
      setWarning('cardEntry_taxInfo_noTaxResidencyInfo_errorMessage');
    }
  };

  const addAddress = useCallback((): void => {
    setTaxFormValues(undefined);
    setShowTaxModal(true);
  }, []);

  const editAddress = useCallback(
    (item?: TableRow): void => {
      setTaxFormValues(taxes.find((tax) => tax.foreignTaxNumberId === item?.id));
      setShowTaxModal(true);
    },
    [taxes]
  );

  const deleteAddress = useCallback((item?: TableRow): void => {
    setIsDeleteModalOpen(true);
  }, []);

  const addressActions = useMemo(
    () => ({
      add: addAddress,
      edit: editAddress,
      delete: deleteAddress,
    }),
    [addAddress, deleteAddress, editAddress]
  );

  const handleTaxSubmit = async (tax: Tax): Promise<void> => {
    let newTaxes;
    if (tax?.foreignTaxNumberId) {
      newTaxes = taxes.map((tx) => (tx.foreignTaxNumberId === tax.foreignTaxNumberId ? tax : tx));
    } else {
      warning && setWarning(undefined);
      newTaxes = [{ ...tax, foreignTaxNumberId: uid(), isNew: true }, ...taxes];
    }
    setTaxes(newTaxes);
    setShowTaxModal(false);
    return Promise.resolve();
  };

  const handleDeleteTax = (): void => {
    setTaxes(taxes.filter((tax) => tax.foreignTaxNumberId !== selectedTax?.id));
    setSelectedTax(undefined);
    setIsDeleteModalOpen(false);
  };

  const closeTaxModal = (): void => setShowTaxModal(false);

  const taxTableData: TableRow[] = taxes.map(
    (tax: Tax): TableRow => ({
      id: tax.foreignTaxNumberId || '',
      columns: [
        tax.taxResidenceCountry?.label || '',
        tax.hasTaxNumberProvided?.label || '',
        tax.foreignTaxNumber,
      ],
    })
  );

  const selectedCountries = taxes.map((tax) => tax.taxResidenceCountry?.value);
  const filteredCountries = countries.filter((country) =>
    taxFormValues
      ? !selectedCountries.includes(country.value) ||
        taxFormValues.taxResidenceCountry?.value === country.value
      : !selectedCountries.includes(country.value)
  );

  return (
    <Container>
      <Title>
        <Trans i18nKey="cardEntry_taxInfo_taxInfo_title" />
      </Title>
      {isDeleteModalOpen ? (
        <Modal
          cancelText={t('general_cancel_button')}
          description={t('general_deleteRecordConfirm_text')}
          onCancel={() => setIsDeleteModalOpen((state) => !state)}
          onSubmit={handleDeleteTax}
          submitText={t('general_components_confirm_button')}
          title="Confirmation"
          type={ModalType.Confirmation}
        />
      ) : null}
      {showTaxModal ? (
        <TaxModal
          countries={filteredCountries}
          formValues={taxFormValues}
          onClose={closeTaxModal}
          submit={handleTaxSubmit}
        />
      ) : null}
      <Form onSubmit={handleSubmit(onSubmit)}>
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="taxNumber"
              render={({ onChange, value }) => (
                <TextInput
                  disabled={disablePage}
                  errorMessage={t(errors?.taxNumber?.message as string)}
                  formatter={formatNumeric}
                  label={t('cardEntry_taxInfo_inn_label')}
                  maxLength={12}
                  name="taxNumber"
                  onChange={onChange}
                  value={value}
                />
              )}
            />
          </FormElement>
        </FormRow>
        <Separator />
        <Subtitle>
          <Trans i18nKey="cardEntry_taxInfo_taxResidency_subtitle" />
        </Subtitle>
        <TableWrapper>
          <Table
            actionConfig={addressActions}
            addActionText={t('general_add_button')}
            disabled={disablePage}
            emptyTableText={t('cardEntry_taxInfo_noTaxResidencyTable_errorMessage')}
            headers={taxTableHeaders.map((head) => t(head))}
            items={taxTableData}
            loading={loadingTable}
            onSelect={setSelectedTax}
            pageNumber={1}
            selectedItem={selectedTax}
            widths={['40', '30', '30']}
          />
        </TableWrapper>
        <WarningText>
          <Trans i18nKey={warning} />
        </WarningText>
        <FormRow>
          <Button
            color={ButtonColor.Primary}
            disabled={formState.isSubmitting}
            size={ButtonSize.Medium}
            text={t('general_continue_button')}
            type="submit"
          />
        </FormRow>
      </Form>
    </Container>
  );
};

export default TaxInformation;
